import { motion } from "framer-motion";
import { ArrowUpRight, BookOpen, Star } from "lucide-react";
import { useSection, useSettings, resolveUrl } from "../lib/useCMS";

const FALLBACK = {
  overline_label: "Published · By Coach Mike",
  title_top: "A Defenseman's",
  title_middle: "Guide to",
  title_accent: "Elite Play.",
  description_main: "A no-nonsense development manual for defensemen who want results, not motivational slogans. Written by former professional defenseman, 20-year military veteran, certified personal trainer and nutrition coach, Mike Fournier.",
  description_secondary: "This is not a systems book. This is not a highlight-reel guide. It's a truth manual for players who want to earn trust, ice time, and responsibility.",
  rating_text: "For defensemen, parents, and coaches.",
  primary_cta_label: "Buy on Amazon",
  primary_cta_url: "https://www.amazon.com/dp/B0G9DS77JS?ref=cm_sw_r_ffobk_cso_sms_mwn_dp_RP9GWC0WYYB30XCRCMXP",
  secondary_cta_label: "Ask Coach Mike",
  image_url: "https://static.wixstatic.com/media/77bacb_1a9f2b80097044cfb1a774b4f7627a60~mv2.jpg/v1/fill/w_544,h_810,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/77bacb_1a9f2b80097044cfb1a774b4f7627a60~mv2.jpg",
};

export const Book = () => {
  const b = useSection("book", FALLBACK);
  const settings = useSettings({ amazon_book_url: FALLBACK.primary_cta_url, contact_email: "mike@builtdifferenthockey.com" });
  const amazonUrl = b.primary_cta_url || settings.amazon_book_url || FALLBACK.primary_cta_url;

  return (
    <section id="book" data-testid="book-section" className="relative bg-[#0f0f0f] py-24 md:py-32 overflow-hidden border-t border-white/5">
      <div className="absolute -right-20 top-16 font-display text-[10rem] leading-none uppercase text-outline opacity-40 pointer-events-none hidden md:block">
        Elite Play
      </div>

      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-x-0 gap-y-10 md:gap-10 items-center">
          <motion.div initial={{ opacity: 0, x: -40, rotate: -5 }} whileInView={{ opacity: 1, x: 0, rotate: -4 }} viewport={{ once: true }} transition={{ duration: 0.9 }} className="col-span-12 md:col-span-5 flex justify-center">
            <div className="relative group">
              <div className="absolute -inset-4 bg-brand-light/30 blur-2xl group-hover:bg-brand-light/50 transition-colors duration-500" />
              <div className="relative">
                <div className="absolute -bottom-4 -right-4 h-full w-full bg-brand" />
                <img src={resolveUrl(b.image_url)} alt="A Defenseman's Guide to Elite Play — book by Coach Mike Fournier" loading="lazy" decoding="async" className="relative h-[440px] md:h-[540px] w-auto object-contain shadow-2xl" />
              </div>
            </div>
          </motion.div>

          <div className="col-span-12 md:col-span-7">
            <div className="flex items-center gap-3 overline">
              <BookOpen className="h-4 w-4" />
              {b.overline_label}
            </div>
            <h2 className="font-display text-5xl md:text-6xl uppercase mt-5 leading-[0.9]" data-testid="book-heading">
              {b.title_top}
              <br />
              {b.title_middle} <span className="text-brand-light">{b.title_accent}</span>
            </h2>
            <p className="mt-6 text-zinc-300 leading-relaxed max-w-xl">{b.description_main}</p>
            <p className="mt-4 text-zinc-400 text-sm max-w-xl">{b.description_secondary}</p>

            <div className="mt-6 flex items-center gap-2 text-sm text-zinc-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-brand-light text-brand-light" />
              ))}
              <span className="ml-1">{b.rating_text}</span>
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <a href={amazonUrl} target="_blank" rel="noopener noreferrer" data-testid="book-amazon-button" className="btn-primary">
                {b.primary_cta_label} <ArrowUpRight className="h-5 w-5" />
              </a>
              <a href={`mailto:${settings.contact_email}?subject=About%20the%20Book`} data-testid="book-inquiry-button" className="btn-ghost">
                {b.secondary_cta_label}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
